import type { CSSProperties, ReactEventHandler } from 'react';

import { apiAsset } from '@/config';

// Compatibility shim for `next/image`. The app never used Next's image
// optimisation (remote hosts, `unoptimized`), so a plain <img> renders the
// exact same box. `fill` reproduces Next's absolute positioning so the
// surrounding `relative` container + `object-cover` classes behave identically.
export interface ImageProps {
  src: string;
  alt?: string;
  fill?: boolean;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  unoptimized?: boolean;
  sizes?: string;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  loader?: unknown;
  onError?: ReactEventHandler<HTMLImageElement>;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  [key: string]: unknown;
}

export default function Image({
  src,
  alt = '',
  fill,
  width,
  height,
  className,
  style,
  priority,
  loading,
  unoptimized: _unoptimized,
  sizes,
  quality: _quality,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  loader: _loader,
  ...rest
}: ImageProps) {
  const resolvedStyle: CSSProperties = fill
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%', ...style }
    : { ...style };

  return (
    <img
      src={typeof src === 'string' ? apiAsset(src) : ''}
      alt={alt}
      width={fill ? undefined : (width as number | undefined)}
      height={fill ? undefined : (height as number | undefined)}
      sizes={sizes}
      loading={loading ?? (priority ? 'eager' : 'lazy')}
      decoding={priority ? 'sync' : 'async'}
      className={className}
      style={resolvedStyle}
      {...rest}
    />
  );
}
